import { Temporal } from 'temporal-polyfill'

import type {
  FullCalendarEvent,
  ScheduleEvent,
  ScheduleEventId,
} from '../types'
import {
  formatDate,
  formatDateLong,
  formatDayOfWeek,
  formatTime,
  getMidnightToday,
  getYesterday,
  parseToZonedDateTime,
} from './dateTime'
import {
  createRRuleSet,
  getFirstOccurrence,
  getNextOccurrence,
  hasIrregularRepeat,
  rruleToText,
} from './rruleHelpers'

/**
 * ScheduleEventHelper - Utility class for processing schedule events
 * Ported from legacy PHP ScheduleEvent class in edit_schedules.php
 */
export class ScheduleEventHelper {
  private scheduleTimeZone: string
  private eventTimeZone: string
  private initialData: ScheduleEvent
  private temporalDuration: Temporal.Duration
  public zonedDateTimeStart: Temporal.ZonedDateTime
  public zonedDateTimeEnd: Temporal.ZonedDateTime
  public plainStartDateTime: Temporal.PlainDateTime
  public plainEndDateTime: Temporal.PlainDateTime

  constructor(eventData: ScheduleEvent, scheduleTimeZone: string) {
    this.scheduleTimeZone = scheduleTimeZone
    this.initialData = eventData
    this.eventTimeZone = this.getTimeZone()
    this.temporalDuration = Temporal.Duration.from(this.getDurationString())

    if (
      (this.initialData.rrule || this.initialData.rdate) &&
      !this.hasEventEnded()
    ) {
      if (this.isSimpleWeeklyHoursRecurrence()) {
        this.zonedDateTimeStart = this.getWeeklyHoursOccurrenceForCurrentWeek()
      } else {
        this.zonedDateTimeStart = this.getZonedDateNextOccurrence()
      }
      this.zonedDateTimeEnd = this.zonedDateTimeStart.add(this.temporalDuration)
    } else {
      this.zonedDateTimeStart = this.getZonedStartDateTime()
      this.zonedDateTimeEnd = this.zonedDateTimeStart.add(this.temporalDuration)
    }

    this.plainStartDateTime = this.zonedDateTimeStart.toPlainDateTime()
    this.plainEndDateTime = this.zonedDateTimeEnd.toPlainDateTime()
  }

  /**
   * Check if this is a new event (no ID)
   */
  isNewEvent(): boolean {
    return !this.initialData.id
  }

  /**
   * Check if the event has ended (end date is in the past)
   */
  hasEventEnded(): boolean {
    try {
      let eventEndTime: Temporal.Instant
      if (this.initialData.end) {
        eventEndTime = Temporal.Instant.from(this.initialData.end)
      } else {
        // Asset responses may omit the derived `end` field. A non-recurring
        // event still has a deterministic end from dtstart + duration, so do
        // not leave past overrides visible forever. Recurring events require
        // occurrence expansion and remain eligible when no terminal bound is
        // supplied.
        if (this.initialData.rrule || this.initialData.rdate) {
          return false
        }
        eventEndTime = this.getZonedStartDateTime()
          .add(this.temporalDuration)
          .toInstant()
      }
      const now = Temporal.Now.instant()
      return Temporal.Instant.compare(eventEndTime, now) <= 0
    } catch {
      // Invalid backend timestamp should not crash rendering.
      // Treat as not-ended so consumers can continue safely.
      return false
    }
  }

  /**
   * Get the duration string, defaulting to 1 day
   */
  private getDurationString(): string {
    if (this.initialData?.duration) {
      return this.initialData.duration
    }

    if (this.initialData?.dtstart && this.initialData?.dtend) {
      try {
        const start = parseToZonedDateTime(
          this.initialData.dtstart,
          this.eventTimeZone,
          this.scheduleTimeZone,
        )
        const end = parseToZonedDateTime(
          this.initialData.dtend,
          this.eventTimeZone,
          this.scheduleTimeZone,
        )

        if (Temporal.ZonedDateTime.compare(end, start) > 0) {
          return start.until(end).toString()
        }
      } catch {
        // Fall back to default duration.
      }
    }

    return 'P1D'
  }

  /**
   * Get the event's timezone, falling back to schedule timezone
   */
  private getTimeZone(): string {
    if (this.initialData?.time_zone) {
      return this.initialData.time_zone
    }
    return this.scheduleTimeZone
  }

  /**
   * Get the start datetime as a ZonedDateTime
   */
  getZonedStartDateTime(dateString?: string): Temporal.ZonedDateTime {
    const _dateString = dateString || this.initialData.dtstart

    if (!_dateString) {
      return getMidnightToday(this.scheduleTimeZone)
    }

    return parseToZonedDateTime(
      _dateString,
      this.eventTimeZone,
      this.scheduleTimeZone,
    )
  }

  /**
   * Check if this event has irregular repeat patterns (rdate or exdate)
   */
  hasIrregularRepeat(): boolean {
    return hasIrregularRepeat(this.initialData.rdate, this.initialData.exdate)
  }

  /**
   * Get the next occurrence of this recurring event
   */
  getZonedDateNextOccurrence(): Temporal.ZonedDateTime {
    const { rrule, rdate, exdate } = this.initialData
    const zonedDateTimeStart = this.getZonedStartDateTime()

    if (rrule || rdate || exdate) {
      const instant = zonedDateTimeStart.toInstant()
      const startTimeDate = new Date(instant.epochMilliseconds)

      const rruleSet = createRRuleSet(
        rrule,
        rdate,
        exdate,
        startTimeDate,
        this.eventTimeZone,
        zonedDateTimeStart.hour,
        zonedDateTimeStart.minute,
      )

      const nextOccurrenceStart = getNextOccurrence(rruleSet, getYesterday())

      if (nextOccurrenceStart) {
        return this.getZonedStartDateTime(nextOccurrenceStart.toISOString())
      }
      return zonedDateTimeStart
    }

    return zonedDateTimeStart
  }

  /**
   * Get the first occurrence of this recurring event
   */
  getZonedDateFirstOccurrence(): Temporal.ZonedDateTime {
    if (this.isSimpleWeeklyHoursRecurrence()) {
      // Weekly hours are stored as a local wall-time template; keep that
      // template day/time for stable Mon-Sun placement in the editor.
      return this.getZonedStartDateTime()
    }

    const { rrule, rdate, exdate } = this.initialData
    const zonedDateTimeStart = this.getZonedStartDateTime()

    if (rrule || rdate || exdate) {
      const instant = zonedDateTimeStart.toInstant()
      const startTimeDate = new Date(instant.epochMilliseconds)

      const rruleSet = createRRuleSet(
        rrule,
        rdate,
        exdate,
        startTimeDate,
        this.eventTimeZone,
        zonedDateTimeStart.hour,
        zonedDateTimeStart.minute,
      )

      const firstOccurrence = getFirstOccurrence(rruleSet)

      if (firstOccurrence) {
        return this.getZonedStartDateTime(firstOccurrence.toISOString())
      }
      return zonedDateTimeStart
    }

    return zonedDateTimeStart
  }

  /**
   * Check if this is an all-day event (24h period starting at midnight)
   */
  isAllDayEvent(): boolean {
    const startDate = this.zonedDateTimeStart
    const totalHours = this.temporalDuration.total({
      unit: 'hours',
      relativeTo: startDate,
    })
    const remainingMinutes =
      this.temporalDuration.total({
        unit: 'minutes',
        relativeTo: startDate,
      }) % 60

    const startsAtMidnight = startDate.hour === 0 && startDate.minute === 0
    const is24Period = totalHours % 24 === 0 && remainingMinutes === 0

    return is24Period && startsAtMidnight
  }

  /**
   * Check if this event occurs within a single day
   */
  isSingleDayEvent(): boolean {
    const startDate = this.zonedDateTimeStart
    const endDate = this.zonedDateTimeEnd

    const isWithinSameDay =
      startDate.day === endDate.day &&
      startDate.month === endDate.month &&
      startDate.year === endDate.year

    const isAllDay =
      this.isAllDayEvent() &&
      this.temporalDuration.total({ unit: 'days' }) === 1

    return isWithinSameDay || isAllDay
  }

  /**
   * Get human-readable recurrence description
   */
  repeatToText(): string {
    if (
      !this.initialData.exdate &&
      !this.initialData.rdate &&
      this.initialData.rrule
    ) {
      return rruleToText(this.initialData.rrule)
    }
    return ''
  }

  /**
   * Get human-readable next occurrence text
   */
  nextOccurrenceToText(): string {
    const nextOccurrence = this.getZonedDateNextOccurrence()
    const startDay = formatDayOfWeek(nextOccurrence)
    const startFormatted = formatDate(nextOccurrence)
    return `${startDay}, ${startFormatted}`
  }

  /**
   * Get human-readable date range text
   */
  dateRangeToText(): string {
    const startFormatted = formatDateLong(this.zonedDateTimeStart)
    const displayedEnd = this.isAllDayEvent()
      ? this.zonedDateTimeEnd.subtract({ days: 1 })
      : this.zonedDateTimeEnd
    const endFormatted = formatDateLong(displayedEnd)
    const startTime = formatTime(this.zonedDateTimeStart)
    const endTime = formatTime(this.zonedDateTimeEnd)

    if (this.isSingleDayEvent()) {
      if (this.isAllDayEvent()) {
        return `Closed • ${startFormatted}`
      }
      return `Closed • ${startFormatted} ${startTime} - ${endTime}`
    }

    if (this.isAllDayEvent()) {
      return `Closed • ${startFormatted} - ${endFormatted}`
    }

    return `Closed • ${startFormatted} ${startTime} - ${endFormatted} ${endTime}`
  }

  /**
   * Get human-readable hours text
   */
  hoursToText(): string {
    if (this.isAllDayEvent()) {
      return 'Closed All Day'
    }

    const startTime = formatTime(this.zonedDateTimeStart)
    const endTime = formatTime(this.zonedDateTimeEnd)

    return `Closed From ${startTime} to ${endTime}`
  }

  /**
   * Check if this event is currently active
   */
  isCurrentlyActive(): boolean {
    const now = this.getZonedStartDateTime(new Date().toISOString())
    return (
      Temporal.ZonedDateTime.compare(this.zonedDateTimeStart, now) <= 0 &&
      Temporal.ZonedDateTime.compare(this.zonedDateTimeEnd, now) >= 0
    )
  }

  /**
   * Convert to FullCalendar event format
   */
  getFullCalendarEvent(): FullCalendarEvent {
    const firstOccurrence = this.getZonedDateFirstOccurrence()
    // Anchor all weekly-hours events to a fixed Monday-start week so Mon-Sun
    // headers can render existing schedule slots consistently.
    const anchorWeekStart = Temporal.ZonedDateTime.from({
      year: 2012,
      month: 1,
      day: 2, // Monday
      hour: 0,
      minute: 0,
      second: 0,
      timeZone: this.scheduleTimeZone,
    })
    const startDate = anchorWeekStart
      .add({ days: firstOccurrence.dayOfWeek - 1 })
      .with({
        hour: firstOccurrence.hour,
        minute: firstOccurrence.minute,
        second: firstOccurrence.second,
        millisecond: firstOccurrence.millisecond,
        microsecond: firstOccurrence.microsecond,
        nanosecond: firstOccurrence.nanosecond,
      })
    const endDate = startDate.add(this.temporalDuration)
    const startPlain = startDate.toPlainDateTime()
    const endPlain = endDate.toPlainDateTime()

    return {
      id: String(this.initialData.id),
      title: this.initialData.summary || 'Open',
      // Use concrete Date objects to avoid parser ambiguity in FullCalendar.
      start: new Date(
        startPlain.year,
        startPlain.month - 1,
        startPlain.day,
        startPlain.hour,
        startPlain.minute,
        startPlain.second,
      ),
      end: new Date(
        endPlain.year,
        endPlain.month - 1,
        endPlain.day,
        endPlain.hour,
        endPlain.minute,
        endPlain.second,
      ),
    }
  }

  /**
   * Weekly-hours events are simple weekly rules without irregular overrides.
   * We treat dtstart as the source-of-truth local wall-time template.
   */
  private isSimpleWeeklyHoursRecurrence(): boolean {
    return (
      this.initialData.fbtype === 'FREE' &&
      Boolean(this.initialData.rrule) &&
      !this.initialData.rdate &&
      !this.initialData.exdate
    )
  }

  /**
   * Build the occurrence in the current week using the dtstart template day/time.
   * This avoids UTC weekday drift for late-day slots.
   */
  private getWeeklyHoursOccurrenceForCurrentWeek(): Temporal.ZonedDateTime {
    const templateStart = this.getZonedStartDateTime()
    const now = Temporal.Now.zonedDateTimeISO(this.scheduleTimeZone)
    const dayOffset = templateStart.dayOfWeek - now.dayOfWeek

    return now.add({ days: dayOffset }).with({
      hour: templateStart.hour,
      minute: templateStart.minute,
      second: templateStart.second,
      millisecond: templateStart.millisecond,
      microsecond: templateStart.microsecond,
      nanosecond: templateStart.nanosecond,
    })
  }

  /**
   * Get the original event data
   */
  getData(): ScheduleEvent {
    return this.initialData
  }

  /**
   * Get the event ID
   */
  getId(): ScheduleEventId {
    return this.initialData.id
  }

  /**
   * Get the event summary/title
   */
  getSummary(): string {
    return this.initialData.summary || ''
  }

  /**
   * Get the fbtype (FREE or BUSY variant)
   */
  getFbtype(): string {
    return this.initialData.fbtype
  }

  /**
   * Check if this is a weekly hours event (FREE type)
   */
  isWeeklyHoursEvent(): boolean {
    return this.initialData.fbtype === 'FREE'
  }

  /**
   * Check if this is an override event (not FREE)
   */
  isOverrideEvent(): boolean {
    return this.initialData.fbtype !== 'FREE'
  }

  /**
   * Get the switchboard routing for this event
   */
  getSwitchboardRouting(): string {
    return this.initialData.switchboard_routing
  }

  /**
   * Check if this is a holiday event
   */
  isHolidayEvent(): boolean {
    return this.initialData.switchboard_routing === 'HOLIDAY'
  }
}
